/**
 * International Business — 6 documents.
 *
 * Every agreement here turns on the law of the counterparty's country: local agent- and
 * distributor-protection concepts, customs and import requirements, labelling and ingredient rules,
 * and trademark rights that have to be secured country by country. This category ships no templates
 * and flags every entry for counsel, because the document that matters is the one drafted against
 * the destination market, not the one adapted from a domestic form.
 */
export const INTERNATIONAL_DOCS = [
  {
    id: "international-sales-agent-agreement",
    name: "International Sales Agent Agreement",
    category: "international",
    subcategory: "Agents and representatives",
    aliases: [
      "Foreign Sales Agent Agreement",
      "Overseas Commission Agent Agreement",
      "Country Agent Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Sales Agent in the Foreign Market",
    whenYouNeedIt:
      "When someone in another country will sell on your behalf for commission rather than buying from you and reselling. Usually the person at a trade show who says they know every buyer in the market.",
    whatItDoes:
      "Appoints an agent to solicit orders in a defined territory at an agreed commission, and sets the territory, term, performance expectations, what the agent may say and sign, and how the relationship ends.",
    whyItMatters:
      "Founders sign a one-page commission letter at a trade show and find out years later that ending it is the expensive part. Many countries have long-standing protections for local sales agents, commonly taking the shape of a payment due on termination, and they are widely understood to be assessed where the agent works rather than where your governing-law clause points. Whether they reach your arrangement is a question for counsel in that country, and it needs asking before the appointment — by the time you want to terminate, the answer is a price rather than a plan.",
    majorTerms: [
      "Territory, and whether the appointment is exclusive",
      "Products covered",
      "Commission rate and the basis it is calculated on",
      "When commission is earned: order taken, shipment, or cash collected",
      "Treatment of direct orders originating in the territory",
      "House accounts and carve-outs",
      "Minimum performance targets",
      "Term and renewal",
      "Termination rights and notice periods",
      "Post-termination commission on pipeline orders",
      "Authority: solicit only, or power to bind you",
      "Expenses, samples and marketing support",
      "Confidentiality and permitted use of your marks",
      "Governing law and dispute resolution",
    ],
    clausesToWatch: [
      {
        clause: "Whether the appointment is exclusive",
        why: "An exclusive appointment in a country whose buyer landscape you do not know can lock the entire market behind one relationship you cannot assess.",
      },
      {
        clause: "When commission is earned",
        why: "Commission on orders taken rather than on cash collected means you pay the agent for sales that were never paid for.",
      },
      {
        clause: "Termination and what it costs",
        why: "What ending the appointment costs may be shaped by concepts in the agent's own country rather than by your contract, and local counsel should price that before you appoint.",
      },
      {
        clause: "Post-termination commission tail",
        why: "Without a stated cut-off the agent can claim commission on orders arriving long after the relationship ended.",
      },
      {
        clause: "Authority to bind",
        why: "An agent who can accept orders or make commitments for you can create obligations you never priced and cannot easily unwind.",
      },
      {
        clause: "Performance minimums",
        why: "Without them an exclusive agent has no obligation to do anything, and you cannot appoint anybody else in the meantime.",
      },
      {
        clause: "Governing law and dispute resolution",
        why: "A clause naming your home courts may not deliver what you expect when the agent, the customers and the assets are all somewhere else.",
      },
      {
        clause: "Use and registration of your marks",
        why: "An agent using or filing your brand locally can create a problem far larger and longer-lasting than any commission dispute.",
      },
    ],
    cpgIssues: [
      "An agent cannot sell what cannot be sold: ingredient permissibility, additive limits, fortification levels and claim rules differ by country and can make your product unsellable as formulated.",
      "Labelling and language requirements differ market by market, and a relabel is a packaging run with its own minimum order and lead time.",
      "Shelf life on arrival is a hard constraint in export, because ocean freight, clearance and local distribution can consume a large share of the code before the product reaches a shelf.",
      "Retail buyers in a new market often require certifications and audit standards you do not hold, and an eager agent will promise them on your behalf.",
      "Sample shipments still cross a border and still need documentation, and samples stuck in customs kill launch momentum entirely.",
      "Commission on export sales has to be modelled against export pricing, freight and duty rather than against your domestic margin.",
      "An agent covering several countries in one region is quoting your product to buyers under quite different labelling and ingredient regimes.",
    ],
    founderMistakes: [
      "Appointing an exclusive agent for a whole region on a one-page letter at a trade show, and being unable to move for years.",
      "Paying commission on orders that shipped and were never paid for.",
      "Never asking local counsel what terminating the appointment would cost, and finding out at the moment of termination.",
      "Letting the agent register the brand locally as a convenience, and losing control of the mark in that market.",
      "Promising retail buyers a product that could not be sold there as formulated or labelled.",
      "Modelling commission against domestic margin and losing money on every export order once freight and duty were loaded.",
    ],
    negotiationPoints: [
      "Ask local counsel in the agent's country what the arrangement costs to end, before you appoint anyone.",
      "Start non-exclusive, or make exclusivity conditional on stated performance and limited in time.",
      "Earn commission on cash collected rather than on orders taken.",
      "Limit the agent's authority to soliciting orders you accept, with no power to commit you.",
      "Set a defined post-termination commission tail rather than leaving it open-ended.",
      "State expressly that the agent may not register or apply for your marks anywhere.",
      "Confirm the product's ingredient, labelling and claim position in the market before appointing.",
    ],
    redFlags: [
      "An exclusive appointment with no performance minimums and no time limit.",
      "No advice taken on what termination costs in the agent's own country.",
      "Commission earned on orders rather than on payments received.",
      "An agent authorised to accept orders or make commitments on your behalf.",
      "Silence on trademark registration and use of your marks.",
      "An open-ended post-termination commission entitlement.",
    ],
    beforeYouSign: [
      "Local counsel's view on agent protections and termination cost in that country.",
      "Confirmation of your trademark position in the territory, before the agent starts using the brand.",
      "A landed-cost and margin model for the market including freight, duty and commission.",
      "A written assessment of whether the product can be sold there as currently formulated and labelled.",
    ],
    related: [
      "international-sales-rep-agreement",
      "international-distributor-agreement",
      "importer-agreement",
      "export-agent-agreement",
      "international-trademark-license-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Agency arrangements are shaped by the law of the country where the agent works, including protections that can make termination far more expensive than the contract suggests, so local counsel should price it before the appointment.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "importer-agreement",
    name: "Importer Agreement",
    category: "international",
    subcategory: "Import and export",
    aliases: [
      "Import Distribution Agreement",
      "Importer of Record Agreement",
      "In-Market Importer Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Importer in the Destination Country",
    whenYouNeedIt:
      "When a company in another country will buy your product, bring it through customs and place it into that market — usually the first serious international order you receive.",
    whatItDoes:
      "Sets the terms on which the importer buys from you and takes product into its market: price and incoterms, who is the importer of record, who handles labelling and any local registration, minimum volumes, territory, and what happens on termination.",
    whyItMatters:
      "The importer is usually the party that formally brings the product into the country, holds any local registrations, arranges the compliant label and deals with the authorities — which means they hold a great deal that is hard to get back. The hazard every founder should know is well documented: a brand that ships into a new country without registering its own trademark there can find the mark already registered by its own importer or distributor. Recovering the position afterwards is slow, expensive and uncertain, and the fix is cheap only before you ship.",
    majorTerms: [
      "Territory and exclusivity",
      "Products covered",
      "Price, currency and incoterms",
      "Payment terms and payment security",
      "Minimum purchase volumes and forecasting",
      "Who acts as importer of record",
      "Responsibility for labelling, translation and any local registration",
      "Ownership of local registrations obtained",
      "Trademark position and an express bar on the importer filing your marks",
      "Marketing spend and brand control",
      "Term, renewal and termination",
      "Post-termination: inventory, registrations, customer relationships",
      "Product liability, insurance and recall cooperation",
      "Governing law and dispute resolution",
    ],
    clausesToWatch: [
      {
        clause: "Who owns any local product registrations",
        why: "Registrations held in the importer's name can turn changing importer into a restart rather than a transfer, and the restart takes months you do not have.",
      },
      {
        clause: "Trademark registration in the territory",
        why: "An importer that registers your brand in its own name controls your access to that market, which is a common and well-documented pattern rather than a rare accident.",
      },
      {
        clause: "Exclusivity against minimum volumes",
        why: "Exclusivity with no volume obligation gives away a whole country and gets nothing measurable back.",
      },
      {
        clause: "Incoterms and the named place",
        why: "The incoterm decides who pays freight, insurance, duty and clearance and where risk passes, so one line changes your landed economics completely.",
      },
      {
        clause: "Payment terms and security",
        why: "Collecting an unpaid invoice across a border is a slower and more expensive problem than collecting one at home.",
      },
      {
        clause: "Termination and transition",
        why: "Without an obligation covering registrations, inventory and customers, exiting the relationship can mean exiting the market.",
      },
      {
        clause: "Labelling responsibility",
        why: "Whoever produces the local label controls what your product says in that market, and errors come back to your brand rather than theirs.",
      },
      {
        clause: "Recall cooperation and traceability",
        why: "A withdrawal in a market where you have no presence depends entirely on the importer's records and willingness to act.",
      },
    ],
    cpgIssues: [
      "Ingredient permissibility, additive limits, fortification rules and permitted claims differ by country and can make a product unsellable as formulated, which is a reformulation project rather than a labelling fix.",
      "Label language, format, nutrition presentation and allergen declaration differ by market, so a compliant local pack is usually a separate SKU with its own artwork, minimum and lead time.",
      "Shelf life is consumed by ocean freight and clearance, so minimum remaining code on arrival should be agreed in writing before the first container.",
      "Temperature-controlled product crossing borders faces delays nobody controls, so cold chain responsibility and the treatment of a compromised container need stating up front.",
      "Local product registration is required in some markets and is slow, and whoever holds it effectively holds your market access.",
      "Case configuration, pallet standards and barcoding differ by market, and a container built to domestic standards can be rejected or reworked at your cost.",
      "Recall and withdrawal in a foreign market runs entirely through the importer's records, so lot traceability across the border has to be agreed rather than assumed.",
    ],
    founderMistakes: [
      "Shipping into a new country without filing the trademark there, and finding the importer had registered it first.",
      "Granting exclusivity for a whole country to the first importer who ordered a container, with no volume commitment attached.",
      "Letting the importer hold the local product registration, and having to start again when the relationship ended.",
      "Agreeing an incoterm they did not understand and absorbing duty, clearance and inland freight they had never priced.",
      "Sending a container with too little remaining shelf life for the transit and clearance time.",
      "Shipping product with a domestic label and having the whole consignment held at the border.",
    ],
    negotiationPoints: [
      "File your trademark in the market before you ship, and bar the importer from applying for it anywhere.",
      "Require any local product registration to be held in your name, or to transfer to you on termination.",
      "Tie exclusivity to minimum volumes and a defined term, with a right to convert to non-exclusive if they are missed.",
      "Choose the incoterm deliberately and model landed cost under it before agreeing price.",
      "Set minimum remaining shelf life on arrival and say who bears short-dated stock.",
      "Agree a transition on termination: registrations, remaining inventory, customer list and artwork.",
      "Secure payment on early orders rather than extending open credit across a border.",
    ],
    redFlags: [
      "Shipping to a market where you have not filed your trademark.",
      "An importer unwilling to hold registrations in your name or to transfer them.",
      "Country-wide exclusivity with no minimum volumes.",
      "No agreed incoterm, or one nobody has modelled.",
      "No minimum remaining shelf life on arrival.",
      "No recall cooperation or lot traceability obligation.",
      "Open credit terms on a first international order.",
    ],
    beforeYouSign: [
      "Your trademark filing position in that country, checked before the first shipment.",
      "Local counsel's view on labelling, ingredient permissibility and registration for your product.",
      "A landed-cost model under the proposed incoterm, including duty, clearance and inland freight.",
      "Realistic transit and clearance times, measured against your product's shelf life.",
      "Payment security appropriate to a first cross-border order.",
    ],
    related: [
      "international-distributor-agreement",
      "international-supply-agreement",
      "export-agent-agreement",
      "international-trademark-license-agreement",
      "international-sales-agent-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Import arrangements turn on the destination country's customs, labelling and registration position and on who holds the trademark there, all of which need local counsel before the first container ships.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "export-agent-agreement",
    name: "Export Agent Agreement",
    category: "international",
    subcategory: "Import and export",
    aliases: [
      "Export Management Company Agreement",
      "EMC Agreement",
      "Export Broker Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Export Agent or Export Management Company",
    whenYouNeedIt:
      "When you engage someone on your side of the border to find and manage overseas buyers, handle export documentation and coordinate shipping — often the only way a small brand exports at all.",
    whatItDoes:
      "Appoints an export agent or management company to develop and service export markets, and sets which markets, on what commission or margin, who owns the customer relationships, and who handles documentation, freight and payment collection.",
    whyItMatters:
      "An export agent solves the practical problem — documentation, freight, buyer contacts — and creates a structural one: whoever owns the relationship with the overseas buyer owns your access to that market. If the agent contracts with buyers in its own name, holds the registrations and controls the customer list, ending the agreement can mean losing every market they built. Getting paid is also a separate problem from selling, because an export sale is not revenue until the money crosses back.",
    majorTerms: [
      "Markets and territories covered",
      "Whether the agent buys and resells or sells on commission",
      "Commission or margin and how it is calculated",
      "Whose name appears on the contract with the overseas buyer",
      "Ownership of the customer list and the introductions",
      "Export documentation responsibilities",
      "Freight, incoterms and cargo insurance",
      "Payment collection, remittance timing and currency risk",
      "Minimum performance expectations by market",
      "Marketing spend and sample costs",
      "Term, renewal and termination",
      "Post-termination access to customers, registrations and documentation",
      "Confidentiality and use of your marks",
    ],
    clausesToWatch: [
      {
        clause: "Whose name is on the contract with the buyer",
        why: "If the agent sells in its own name, its customers are its customers, and you may have no way to reach them at all.",
      },
      {
        clause: "Ownership of the customer list",
        why: "Without an express right to the list and the introductions, a terminated agent takes the markets they built with them.",
      },
      {
        clause: "Whether the agent buys or brokers",
        why: "An agent buying and reselling controls resale pricing and positioning in markets you cannot observe.",
      },
      {
        clause: "Payment collection and remittance",
        why: "Whoever collects controls the timing of your money, and currency movement between order and remittance can remove the margin entirely.",
      },
      {
        clause: "Incoterms and freight responsibility",
        why: "The term chosen decides who bears cost and risk at each leg, and it often gets set by whoever books the freight rather than by anyone who modelled it.",
      },
      {
        clause: "Post-termination transition",
        why: "Documentation, registrations and buyer contacts are the real assets of the relationship and have to be handed over deliberately.",
      },
      {
        clause: "Performance expectations by market",
        why: "An agent holding a broad territory with no targets can sit on markets they never develop while blocking anyone else.",
      },
      {
        clause: "Use and registration of your marks",
        why: "An agent filing for your brand in a market it is developing creates exactly the same problem as an importer doing so.",
      },
    ],
    cpgIssues: [
      "Export shipments consume shelf life in transit and clearance, so the agent has to be selling product with enough code left to reach a shelf and sell through.",
      "Labelling, language and ingredient rules differ by destination, so an agent quoting several markets is quoting products that may each need their own artwork and possibly their own formulation.",
      "Certificates of origin, of analysis, of free sale and of health are routine in food and beverage export and each takes time, so who obtains them should be stated.",
      "Container loading, pallet standards and case marking differ by destination, and rework at the port is expensive and slow.",
      "Temperature-controlled export needs a specified cold chain and a clear position on who bears the loss when a reefer fails at sea.",
      "Export pricing has to be built from a landed-cost model at the destination rather than from a domestic price list minus a discount.",
      "Minimum export orders are usually full containers, which is a large production and cash commitment for a small brand.",
    ],
    founderMistakes: [
      "Letting an export agent contract with overseas buyers in its own name, and losing every customer when the relationship ended.",
      "Never securing rights to the customer list, and having no way to reach buyers built up over three years.",
      "Quoting an export price from the domestic list and discovering the margin had gone once freight, duty and commission were loaded.",
      "Shipping product whose remaining shelf life could not survive transit, clearance and a local sales cycle.",
      "Accepting an incoterm chosen by the freight forwarder and absorbing costs nobody had modelled.",
      "Treating a signed order as revenue, then waiting months for payment from a buyer they had no practical way to chase.",
    ],
    negotiationPoints: [
      "Require contracts with overseas buyers to be in your name, or make the customer list expressly yours and transferable.",
      "Set market-by-market performance expectations rather than granting a broad territory outright.",
      "Choose the incoterm deliberately and model landed cost under it before quoting anyone.",
      "Agree who obtains export certificates and documentation, and who pays for them.",
      "Set payment terms and remittance timing appropriate to a cross-border sale, and address currency.",
      "Bar the agent from registering your marks in any market.",
      "Agree a transition on termination: documentation, registrations, buyer contacts and pending orders.",
    ],
    redFlags: [
      "An agent selling to overseas buyers in its own name with no obligation to disclose them.",
      "No right to the customer list on termination.",
      "A broad multi-country territory with no performance expectations.",
      "No agreed position on export certificates and documentation.",
      "Payment collected by the agent with no stated remittance timing.",
      "Silence on trademark filings in the markets being developed.",
    ],
    beforeYouSign: [
      "A landed-cost model for the first target market under the proposed incoterm.",
      "Your trademark filing position in the markets the agent intends to develop.",
      "The certificates and documentation your product needs in those markets, and who obtains each.",
      "Your shelf life measured against realistic transit, clearance and sell-through time.",
    ],
    related: [
      "importer-agreement",
      "international-sales-agent-agreement",
      "international-supply-agreement",
      "freight-broker-agreement",
      "logistics-services-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Who contracts with the overseas buyer, who holds any registrations and where you can realistically be paid all turn on the destination market's rules, so local advice belongs alongside your own counsel's review.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "international-manufacturing-agreement",
    name: "International Manufacturing Agreement",
    category: "international",
    subcategory: "Cross-border manufacturing and supply",
    aliases: [
      "Overseas Contract Manufacturing Agreement",
      "Offshore Co-Packing Agreement",
      "Foreign Manufacturing Agreement",
    ],
    stages: ["manufacturing", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Overseas Manufacturer",
    whenYouNeedIt:
      "When your product, or a component of it, is made outside your home country: a gummy manufacturer abroad, an overseas powder blender, a foreign bottler, or packaging produced in another market.",
    whatItDoes:
      "Sets the terms on which an overseas manufacturer produces your product — specification, quality, ownership of formula and tooling, pricing and currency, lead times, inspection rights, and what happens when a run fails.",
    whyItMatters:
      "Everything that makes a domestic co-packing relationship risky is harder across a border: holding a supplier to a specification, inspecting a run before it ships, recovering a deposit, moving production elsewhere, and protecting a formula in a country whose approach you have not tested. Add eight to twelve weeks of ocean freight to every mistake and a failed run costs a season rather than a week. This is where founders most often discover that a contract written for a domestic relationship gives them very little practical leverage where the factory actually sits.",
    majorTerms: [
      "Product specification and acceptance standards",
      "Quality agreement and the testing regime",
      "Pre-shipment inspection rights and who performs them",
      "Formula, artwork and tooling ownership",
      "Confidentiality and non-use of your formula",
      "MOQ, pricing, currency and price adjustment",
      "Deposits, payment milestones and payment security",
      "Lead times, scheduling and capacity priority",
      "Incoterms and who arranges freight",
      "Certificates and documentation needed at destination",
      "Ingredient sourcing, substitution and traceability",
      "Recall cooperation and cost allocation",
      "Term, exclusivity and termination",
      "Transition assistance and return of tooling and specifications",
      "Governing law, controlling language and dispute resolution",
    ],
    clausesToWatch: [
      {
        clause: "Pre-shipment inspection",
        why: "A container inspected only on arrival is a container you have already paid to ship before finding out whether the run is right.",
      },
      {
        clause: "Formula and tooling ownership",
        why: "Recovering a formula, artwork or a mould from a factory in another country is a practical problem long before it is a legal one, so keep the specification and the tooling where you can actually reach them.",
      },
      {
        clause: "Deposits and payment milestones",
        why: "Money paid up front to a factory abroad is very hard to get back if the run fails, so payments should follow inspection rather than lead it.",
      },
      {
        clause: "Which language controls",
        why: "A contract executed in two languages with no stated controlling version produces an argument about what was agreed before anyone argues about the product.",
      },
      {
        clause: "Dispute resolution that can be acted on locally",
        why: "Naming your home courts may produce an outcome of little practical use where the factory and its assets sit, which local counsel should advise on before signing.",
      },
      {
        clause: "Capacity and scheduling priority",
        why: "A small brand's run is the first to be bumped when a large customer needs the line, and lead times abroad leave no room to recover.",
      },
      {
        clause: "Ingredient sourcing and substitution",
        why: "A factory changing an ingredient source without notice changes your product, your label and possibly whether it can be sold in your own market.",
      },
      {
        clause: "Recall cooperation and records",
        why: "A defective run made abroad still becomes a domestic recall, and you will need lot traceability and cooperation from a party a very long way away.",
      },
    ],
    cpgIssues: [
      "Ingredient specifications, allergen controls and facility standards vary widely, and a foreign facility's certifications need verifying rather than accepting as an emailed PDF.",
      "Allergen changeover and shared-line practice in an unfamiliar facility is hard to audit remotely, and an undeclared allergen is the failure that ends brands.",
      "Certificates of analysis, of origin and of free sale are needed to import back into your own market, and getting them wrong holds the container at the border.",
      "Lot coding and traceability formats have to work for your home market's requirements, not only for the factory's own internal system.",
      "Ocean freight adds weeks to every cycle, so shelf life has to be measured from production date with the transit built in from the start.",
      "Tooling, moulds and change parts made abroad are physical assets sitting in someone else's building, and getting them back is a negotiation rather than a right you can exercise quickly.",
      "Currency movement between order and payment can move your cost of goods materially over a long lead time.",
      "A single overseas manufacturer with no qualified alternative is a real continuity risk once freight, customs and lead time are added to any switch.",
    ],
    founderMistakes: [
      "Paying a large deposit up front and losing it when the first run failed specification.",
      "Accepting facility certifications on paper and never having anyone inspect the plant.",
      "Skipping pre-shipment inspection to save cost, and paying ocean freight on a container of unsaleable product.",
      "Signing a bilingual contract with no controlling language, and arguing about the translation instead of the defect.",
      "Leaving the formula and the tooling both with the factory, and being unable to move production when the relationship soured.",
      "Not building transit time into shelf life, so product arrived with too little code left for retail.",
    ],
    negotiationPoints: [
      "Tie payments to inspection milestones rather than paying up front.",
      "Require pre-shipment inspection by an independent inspector you appoint, with a right to reject before the container ships.",
      "State that the formula, artwork, specifications and tooling are yours, and set out exactly how they come back.",
      "Name a controlling language for the contract and for the specification.",
      "Take local advice on a dispute resolution route that can actually be acted on where the factory is.",
      "Require notice and written approval before any ingredient source or process change.",
      "Agree lot coding, documentation and certificate formats your home market needs before the first run.",
      "Qualify a second manufacturer before you are wholly dependent on the first.",
    ],
    redFlags: [
      "A large deposit with no inspection milestone attached.",
      "No pre-shipment inspection right.",
      "Formula, artwork or tooling held by the factory with no return mechanism.",
      "A bilingual contract with no controlling version.",
      "Facility certifications nobody has verified on the ground.",
      "No notice requirement for ingredient or process changes.",
      "No recall cooperation or lot traceability obligation.",
    ],
    beforeYouSign: [
      "An on-the-ground audit of the facility, or an independent audit report you actually trust.",
      "Local counsel's advice on dispute resolution and on protecting your formula where the factory sits.",
      "A landed-cost model including freight, duty, inspection and currency assumptions.",
      "Your specification, quality agreement and lot-coding requirements agreed in writing before the first run.",
    ],
    related: [
      "co-packer-agreement",
      "contract-manufacturing-agreement",
      "quality-agreement",
      "formula-ownership-agreement",
      "international-supply-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Holding a factory to a specification, protecting a formula and recovering a deposit all depend on the law and the practical realities where the plant sits, so local counsel belongs alongside your own before the first purchase order.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "international-supply-agreement",
    name: "International Supply Agreement",
    category: "international",
    subcategory: "Cross-border manufacturing and supply",
    aliases: [
      "Cross-Border Supply Agreement",
      "Foreign Ingredient Supply Agreement",
      "Overseas Purchase Agreement",
    ],
    stages: ["manufacturing", "distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Overseas Supplier or Overseas Buyer",
    whenYouNeedIt:
      "When you buy ingredients, packaging or finished goods from a supplier in another country, or supply your own product across a border on standing terms rather than order by order.",
    whatItDoes:
      "Sets the standing terms for cross-border supply: specification, volumes and forecasts, price and currency, incoterms, documentation, inspection and rejection, lead times, and what happens when a shipment is late, short or non-conforming.",
    whyItMatters:
      "Cross-border supply concentrates three risks a domestic agreement barely has to think about. The incoterm decides who bears cost and risk on every leg, and a term chosen casually moves real money on every container. Documentation decides whether the goods clear at all, or sit on a quay accruing charges. And getting paid — or getting money back — across a border is a materially different problem from doing so at home, which is why payment security often matters more than the price you negotiated.",
    majorTerms: [
      "Products and agreed specification",
      "Volumes, forecasts and whether they bind either party",
      "Price, currency and adjustment mechanism",
      "Incoterms and the exact named place",
      "Freight, cargo insurance and who arranges each leg",
      "Documentation required for clearance",
      "Duties and tariffs, and who bears a change in them",
      "Inspection, acceptance and rejection process",
      "Lead times and delivery windows",
      "Minimum remaining shelf life on arrival",
      "Payment terms and any security instrument",
      "Non-conforming goods and available remedies",
      "Force majeure covering port and border disruption",
      "Term, termination and transition",
      "Governing law, controlling language and dispute resolution",
    ],
    clausesToWatch: [
      {
        clause: "The incoterm and the named place",
        why: "This single line allocates freight, insurance, duty, clearance and the point where risk passes, and getting it wrong changes your landed cost per container.",
      },
      {
        clause: "Who bears a change in duties or tariffs",
        why: "Rates change, and a long-term price with no allocation of that risk puts it entirely on one party by accident rather than by agreement.",
      },
      {
        clause: "Documentation responsibility",
        why: "Goods that cannot clear are goods sitting on a quay accruing charges while both sides re-read the contract.",
      },
      {
        clause: "Inspection and rejection window",
        why: "A window running from shipment rather than arrival can expire weeks before anyone has actually seen the goods.",
      },
      {
        clause: "Minimum remaining shelf life on arrival",
        why: "Transit and clearance consume code, and left undefined this becomes an argument only after the container has landed.",
      },
      {
        clause: "Payment security",
        why: "An unsecured cross-border sale is a receivable in a place where chasing it is slow, expensive and often impractical.",
      },
      {
        clause: "Currency and price adjustment",
        why: "On long lead times, currency movement between order and payment can exceed the margin on the order.",
      },
      {
        clause: "Force majeure scope",
        why: "The most likely cause of a missed cross-border delivery sits at a port or a border rather than in either party's factory.",
      },
    ],
    cpgIssues: [
      "Ingredient specifications have to be written to your home market's requirements, because a supplier's domestic-standard specification may not be acceptable where you actually sell.",
      "Certificates of analysis, allergen statements and origin documentation have to travel with each lot, and a missing certificate can hold a container even when the product itself is fine.",
      "Minimum remaining shelf life on arrival should be agreed as days or as a share of total code, because ocean freight and clearance consume a meaningful part of it.",
      "Temperature-controlled shipments need a specified cold chain and an agreed position on what happens when a reefer fails or a container is held for inspection.",
      "Packaging supplied from abroad — cans, bottles, cartons, labels — carries long lead times and large minimums, so a specification change can strand a full order in another country.",
      "Ingredient origin matters for labelling claims and for retailer requirements, so a supplier changing origin without notice can invalidate what your pack says.",
      "Container-level minimums make cross-border supply a cash and warehousing commitment rather than simply a purchasing decision.",
    ],
    founderMistakes: [
      "Agreeing a price without understanding the incoterm, and absorbing freight, insurance, duty and inland delivery they had never modelled.",
      "Losing a rejection right because the window ran from shipment and the container took six weeks to arrive.",
      "Paying in full against documents and receiving goods that did not meet specification.",
      "Not agreeing minimum remaining shelf life, and receiving ingredients with a fraction of the code they expected.",
      "Being caught by a duty change on a long fixed price with no adjustment mechanism.",
      "Ordering a container of packaging to a superseded specification and stranding the whole minimum overseas.",
    ],
    negotiationPoints: [
      "Choose the incoterm deliberately, name the exact place, and model landed cost under it before agreeing price.",
      "Allocate the risk of duty and tariff changes expressly rather than leaving it to chance.",
      "Run the inspection and rejection window from arrival, with a stated period to inspect.",
      "Agree minimum remaining shelf life on arrival in days or as a share of total code.",
      "Use payment security appropriate to the order size and the relationship's history on early shipments.",
      "Include a price adjustment or currency mechanism where lead times are long.",
      "Extend force majeure to port congestion, border closure and carrier failure.",
    ],
    redFlags: [
      "No stated incoterm, or one nobody has modelled.",
      "A rejection window running from shipment rather than arrival.",
      "Full payment against documents with no inspection right.",
      "No minimum remaining shelf life on arrival.",
      "A long fixed price with no duty, tariff or currency mechanism.",
      "No documentation schedule for clearance at destination.",
    ],
    beforeYouSign: [
      "A landed-cost model per container under the exact incoterm and named place proposed.",
      "The documentation and certificates required to clear at destination, listed and assigned to a party.",
      "Realistic transit and clearance times, measured against the product's shelf life.",
      "Payment security appropriate to the order size and the counterparty's track record.",
    ],
    related: [
      "international-manufacturing-agreement",
      "importer-agreement",
      "master-supply-agreement",
      "ingredient-supply-agreement",
      "purchase-order-terms",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Incoterms, clearance documentation, duty allocation and payment security all interact with the destination country's rules, and a term chosen casually moves real money on every container.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "international-trademark-license-agreement",
    name: "International Trademark License Agreement",
    category: "international",
    subcategory: "Cross-border brand rights",
    aliases: [
      "Foreign Brand License Agreement",
      "Cross-Border Trademark License",
      "Territory Brand License",
    ],
    stages: ["growth"],
    importance: "advanced",
    entityTypes: ["any"],
    parties: "Brand Owner (Licensor) + Foreign Licensee",
    whenYouNeedIt:
      "When someone in another country will make or sell product under your brand: a licensed local manufacturer, a market-entry partner, a regional licensee, or a locally produced edition of your product.",
    whatItDoes:
      "Grants a defined right to use your marks in a defined territory for defined products, and sets the quality standards, approval rights, royalties, term, and what happens to the brand and any local registrations when the licence ends.",
    whyItMatters:
      "Trademark rights are secured country by country, so a brand that has not filed in a market has very little to license there and very little to defend with. The hazard is well documented: a brand that ships into a new country without registering its mark can find the mark already registered by its own distributor, importer or licensee, sometimes as an administrative convenience and sometimes not, and recovering the position afterwards is slow, expensive and uncertain. A licence also puts your name on product you did not make, in a market you cannot see, which is why quality control and approval rights matter more here than the royalty rate.",
    majorTerms: [
      "The marks licensed and the registrations they rest on",
      "Territory and whether the licence is exclusive",
      "Products and channels covered",
      "Quality standards and specification control",
      "Approval rights over artwork, claims and marketing",
      "Inspection and audit rights, including of the facility",
      "Royalty rate, royalty base and minimum royalties",
      "Reporting, records and audit of sales",
      "Who files and holds trademark registrations in the territory",
      "Express bar on the licensee registering your marks or similar ones",
      "Enforcement against local infringers and who acts",
      "Term, renewal and termination",
      "Post-termination sell-off, destruction of materials and transfer of registrations",
      "Indemnity, insurance and recall cooperation",
    ],
    clausesToWatch: [
      {
        clause: "Who holds the registrations in the territory",
        why: "Registrations held by or transferred to a licensee can leave you unable to enter that market yourself once the licence ends.",
      },
      {
        clause: "Express bar on the licensee filing your marks",
        why: "Without it, a licensee filing your brand, a variant or a local-language version creates exactly the hazard this category exists to warn about.",
      },
      {
        clause: "Quality control and approval rights",
        why: "Your brand goes on product made to someone else's process, and the only control you have is the one written into the licence.",
      },
      {
        clause: "Approval over claims and marketing",
        why: "What a brand may claim differs by market, and a licensee's local campaign becomes your brand's position in that country.",
      },
      {
        clause: "Royalty base, reporting and audit",
        why: "A royalty on an undefined base with no audit right is a number the licensee gets to choose each quarter.",
      },
      {
        clause: "Minimum royalties or performance",
        why: "An exclusive licence with no minimum lets a licensee sit on a market for the whole term without developing it.",
      },
      {
        clause: "Post-termination sell-off and destruction",
        why: "Without limits, terminated licensees keep selling branded stock long after the relationship ended and while you are trying to re-enter.",
      },
      {
        clause: "Enforcement against local infringers",
        why: "Somebody has to police the market, and if nobody is obliged to, nobody does.",
      },
    ],
    cpgIssues: [
      "A licensed local manufacturer sources ingredients locally, so the specification, allergen controls and permitted substitutions have to be tight or the product diverges from yours.",
      "Claims permitted on your pack at home may not be permitted in the licensee's market, and the reverse is also true — a licensee may make claims you would never allow.",
      "Local formulation changes made for taste, cost or ingredient availability produce a product carrying your name that you would not recognise on a shelf.",
      "Facility standards and certifications differ, so audit rights matter more in a cross-border licence than in a domestic supply relationship.",
      "Product made under licence abroad can find its way back into your home market through grey channels, competing with your own product at a different specification.",
      "A recall of licensed product carries your brand name regardless of who made it, so recall cooperation, lot traceability and insurance need to be explicit.",
      "Packaging artwork adapted locally for language and format drifts from your brand standards and from what your own label review would have allowed.",
    ],
    founderMistakes: [
      "Licensing a market where they had never filed the trademark, and discovering the licensee had filed it first.",
      "Granting an exclusive country licence with no minimum royalty, and losing the market for the length of the term.",
      "Taking no approval right over artwork and marketing, and seeing claims made under their brand they would never have signed off.",
      "Never auditing the licensee's facility or sales records, and having no basis to challenge either quality or royalties.",
      "Agreeing no post-termination limits and watching branded stock sell for two more years.",
      "Finding licensed product from another market on their own domestic shelves at a lower price.",
    ],
    negotiationPoints: [
      "File your marks in the territory before you license anything there.",
      "Require registrations to be held in your name, and bar the licensee from filing your marks or anything similar.",
      "Take approval rights over artwork, claims, marketing and any specification change.",
      "Include facility inspection and sales audit rights, and actually use them.",
      "Set minimum royalties or performance thresholds tied to keeping exclusivity.",
      "Restrict sales to the licensed territory and channels, and address grey-market flow back into your own market.",
      "Set a short, defined post-termination sell-off period and require destruction of remaining branded materials.",
      "Require recall cooperation, lot traceability and insurance naming you.",
    ],
    redFlags: [
      "Licensing a territory where you hold no trademark filing.",
      "No bar on the licensee registering your marks or local variants.",
      "An exclusive licence with no minimum royalty or performance requirement.",
      "No approval rights over artwork, claims or specification.",
      "No audit rights over sales records or the facility.",
      "An open-ended post-termination sell-off.",
      "No restriction on sales outside the licensed territory.",
    ],
    beforeYouSign: [
      "Your trademark filing and registration position in the territory, confirmed by local counsel.",
      "Local counsel's view on what your brand may claim in that market.",
      "The quality standard, specification and audit regime you will actually enforce.",
      "A royalty model with a clearly defined base and a reporting format you can check.",
    ],
    related: [
      "trademark-license-agreement",
      "brand-license-agreement",
      "trademark-assignment",
      "importer-agreement",
      "international-manufacturing-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Trademark rights are secured country by country and a licence is only as strong as the registration behind it, so local counsel should confirm your filing position before any right is granted.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default INTERNATIONAL_DOCS;
